import { loadingReducers, LoadingState } from './base/baseModelStore';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from '@root/store/store';
import { httpClient } from '../infrastructure/http/HttpClient';
import { Feature, FeatureCollection } from '@api-model/models';
import { PipeType } from '@api-model/Enums';
import { Bounds } from 'models/boundsCorners';
import { parseBoundsToCorners } from '@features/dashboard/sites-map/SiteMap.utils';

export interface PipesState extends LoadingState {
  surfacePipes: FeatureCollection;
  undergroundPipes: FeatureCollection;
  properties: { [key: string]: string };
}

const loadingSurfaceSuccess = (state: PipesState, { payload }: PayloadAction<FeatureCollection>) => {
  state.surfacePipes = payload;
  state.loading = false;
};

const loadingUndergroundSuccess = (state: PipesState, { payload }: PayloadAction<FeatureCollection>) => {
  state.undergroundPipes = payload;
  state.loading = false;
};

const loadingPropsSuccess = (state: PipesState, { payload }: PayloadAction<{ [key: string]: string }>) => {
  state.properties = payload;
  state.loading = false;
};

const slice = createSlice({
  name: 'pipesList',
  initialState: {
    loading: false,
    error: null,
    surfacePipes: { type: '', features: [] as Feature[] } as FeatureCollection,
    undergroundPipes: {
      type: '',
      features: [] as Feature[],
    } as FeatureCollection,
    properties: {} as { [key: string]: string },
  } as PipesState,
  reducers: {
    ...loadingReducers,
    loadingSurfaceSuccess,
    loadingUndergroundSuccess,
    loadingPropsSuccess,
  },
});

const actions = slice.actions;

const fetchPipesCollection = (accountId: number, objectType: PipeType, bounds: Bounds): AppThunk => async (
  dispatch
) => {
  const corners = parseBoundsToCorners(bounds);

  try {
    dispatch(actions.startLoading());

    const { data } = await httpClient.get<FeatureCollection>(
      `/geojson/search?accountId=${accountId}&objectType=${objectType}&topLeftLat=${corners.topLeft.lat}&topLeftLng=${corners.topLeft.lng}&bottomRightLat=${corners.bottomRight.lat}&bottomRightLng=${corners.bottomRight.lng}`
    );

    if (data) {
      if (objectType === PipeType.WasteSurfacePipe) {
        dispatch(actions.loadingSurfaceSuccess(data));
      } else {
        dispatch(actions.loadingUndergroundSuccess(data));
      }
    }
  } catch (err) {
    dispatch(actions.loadingFailed(err.toString()));
  }
};

const fetchPropsById = (accountId: number, objectType: PipeType, id: string): AppThunk => async (dispatch) => {
  dispatch(actions.startLoading());

  const { data } = await httpClient.get<{ [key: string]: string }>(`/geojson/${id}/properties?accountId=${accountId}`);

  dispatch(actions.loadingPropsSuccess(data ?? {}));
};

const thunks = { fetchPipesCollection, fetchPropsById };

export const pipesEntity = {
  reducer: slice.reducer,
  actions: actions,
  thunks,
} as {
  reducer: typeof slice.reducer;
  actions: typeof actions;
  thunks: typeof thunks;
};
